import { useQuery } from '@apollo/client';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections';
import { useMemo } from 'react';
import { type Path, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ListRegionsDocument,
  type ListRegionsQuery,
} from '../../../../../../graphql/fragment/audiences/ListRegions.generated';
import { MatchingOperator } from '../../../constants';
import type { AudienceForm, ExcludesGroupName, IncludesGroupName } from '../../../types';
import { useGroupListMarketOperators } from '../../AudienceCriteria/components/groups/hooks/useGroupListMarketOperators';
import { MarketOperatorRestrictionAlert } from '../../AudienceCriteria/components/groups/MarketOperatorRestrictionAlert';
import { OperatorChip, OperatorType } from '../../DimensionChip/OperatorChip';
import { OperatorSelector } from './OperatorSelector';
import { Segments } from './Segments';
import { SubgroupSummary } from './SubgroupSummary';
import { SummaryAlert } from './SummaryAlert';

type SummaryPath = Extract<Path<AudienceForm>, typeof IncludesGroupName | typeof ExcludesGroupName>;

type SummaryProps = {
  sectionName: string;
  listType: 'includes' | 'excludes';
  path: SummaryPath;
  isEdit: boolean;
  isCreate?: boolean;
  isCreateFromTemplate?: boolean;
};

export const Summary = ({ sectionName, path, listType, isEdit, isCreate, isCreateFromTemplate }: SummaryProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<AudienceForm>();

  const observableNormalizedDefinitionAtPath = useWatch({ name: path, control });
  const { data: regionsData } = useQuery<ListRegionsQuery>(ListRegionsDocument);
  const regions = regionsData?.region ?? [];

  const { shouldUseRegionFirstFlow, topLevelMarketOperatorReason, subgroupRestrictionReasonByParentIndex } =
    useGroupListMarketOperators({ fieldName: path, isCreate, isCreateFromTemplate });

  const listBannerMarketOperatorReason = useMemo(() => {
    if (topLevelMarketOperatorReason != null) {
      return topLevelMarketOperatorReason;
    }

    for (const subgroupReason of Object.values(subgroupRestrictionReasonByParentIndex)) {
      if (subgroupReason != null) {
        return subgroupReason;
      }
    }

    return null;
  }, [topLevelMarketOperatorReason, subgroupRestrictionReasonByParentIndex]);

  const sectionOperator =
    observableNormalizedDefinitionAtPath.operator === MatchingOperator.INTERSECTION
      ? OperatorType.AND
      : OperatorType.OR;

  return (
    <Box
      sx={{
        mb: 4,
      }}
    >
      <Typography
        variant="h3"
        color={listType === 'excludes' ? 'error.main' : 'text.primary'}
        sx={{
          mb: 2,
        }}
      >
        {sectionName}
      </Typography>

      {listType === 'includes' && isEdit && shouldUseRegionFirstFlow && listBannerMarketOperatorReason != null && (
        <Box sx={{ mb: 2 }}>
          <MarketOperatorRestrictionAlert reason={listBannerMarketOperatorReason} />
        </Box>
      )}

      <SummaryAlert />

      <Stack spacing={2} sx={{ width: '100%' }}>
        {observableNormalizedDefinitionAtPath.groups.length > 1 && (
          <Box sx={{ width: '100%' }}>
            <OperatorSelector
              isSubgroup={false}
              path={`${path}.operator`}
              readOnly={!isEdit}
              andOperatorDisabled={isEdit && shouldUseRegionFirstFlow && topLevelMarketOperatorReason != null}
            />
          </Box>
        )}

        {observableNormalizedDefinitionAtPath.groups.map((field, parentIndex) => {
          const isSubgroup = 'groups' in field;
          const groupName = t('Group {count}', { count: parentIndex + 1 });
          const isLastItem = parentIndex + 1 === observableNormalizedDefinitionAtPath.groups.length;

          return (
            <Box key={parentIndex} sx={{ width: '100%' }}>
              <Typography
                variant="body1Bold"
                component="span"
                sx={{
                  mb: 1,
                }}
              >
                {groupName}
              </Typography>

              {isLastItem ? (
                isSubgroup ? (
                  <SubgroupSummary
                    subgroup={field}
                    parentIndex={parentIndex}
                    sx={{ pl: 5 }}
                    listType={listType}
                    regions={regions}
                    marketOperatorConfig={{
                      isEdit,
                      shouldUseRegionFirstFlow,
                      subgroupRestrictionReasonByParentIndex,
                    }}
                  />
                ) : (
                  <Segments group={field} sx={{ pl: 5 }} listType={listType} regions={regions} />
                )
              ) : (
                <>
                  <IndentedSections sx={{ ml: 2, pl: 3, pb: 1 }}>
                    {isSubgroup ? (
                      <SubgroupSummary
                        subgroup={field}
                        parentIndex={parentIndex}
                        listType={listType}
                        regions={regions}
                        marketOperatorConfig={{
                          isEdit,
                          shouldUseRegionFirstFlow,
                          subgroupRestrictionReasonByParentIndex,
                        }}
                      />
                    ) : (
                      <Segments group={field} sx={{ pb: 2 }} listType={listType} regions={regions} />
                    )}
                  </IndentedSections>

                  <OperatorChip operator={sectionOperator} readonly hideDivider />

                  <IndentedSections sx={{ ml: 2, pl: 3 }}>
                    <Box sx={{ height: '12px' }} />
                  </IndentedSections>
                </>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
